/**
 * CarePlanPortalPublishPanel — Phase 6.
 *
 * Coordinator-only control that lives on an APPROVED care plan's detail
 * view. Lets the coordinator choose which sections of the approved plan
 * are visible to portal users (participant / family / guardian).
 *
 * Backed by PATCH /api/portal-visibility/careplans/{plan_id}. The endpoint
 * is gated server-side by `portal_goals_visibility_enabled`; this component
 * renders nothing when the flag is OFF.
 *
 * Phase 6 visibility model: per-section opt-in. Nothing is exposed by
 * default — every section the coordinator wants on the portal must be
 * explicitly ticked here.
 */
import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Save } from 'lucide-react';
import { toast } from 'sonner';

const LEGACY_SECTIONS = [
  { key: 'medical_alerts', label: 'Medical Alerts' },
  { key: 'personal_care_needs', label: 'Personal Care Needs' },
  { key: 'important_notes', label: 'Important Notes' },
];

export function usePortalVisibilityFlag() {
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    return !!flags.portal_goals_visibility_enabled;
  } catch { return false; }
}

export function CarePlanPortalPublishPanel({ plan, onSaved }) {
  const enabled = usePortalVisibilityFlag();
  const [selected, setSelected] = useState(new Set(plan?.portal_visible_sections || []));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(new Set(plan?.portal_visible_sections || []));
  }, [plan]);

  if (!enabled) return null;
  if (!plan || plan.status !== 'APPROVED') {
    return (
      <Card data-testid="careplan-portal-publish-locked">
        <CardContent className="p-4 text-xs text-muted-foreground">
          <EyeOff className="inline h-3 w-3 mr-1" />
          Portal publishing is available once this care plan is APPROVED.
        </CardContent>
      </Card>
    );
  }

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const arr = Array.from(selected);
      await api.patch(`/portal-visibility/careplans/${plan.id || plan._id}`, {
        portal_visible_sections: arr,
      });
      toast.success(arr.length ? `Published ${arr.length} section(s) to portal` : 'Removed all sections from portal');
      onSaved?.(arr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update portal visibility');
    } finally { setSaving(false); }
  };

  // Combine legacy section keys (medical_alerts/personal_care_needs/important_notes)
  // with v2 sections (plan.sections[].section_key) if present.
  const v2Sections = (plan.sections || [])
    .filter((s) => s.section_key)
    .map((s) => ({ key: s.section_key, label: s.title || s.section_key }));
  const allOptions = [...LEGACY_SECTIONS, ...v2Sections];

  return (
    <Card data-testid="careplan-portal-publish-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Portal visibility</h3>
          </div>
          <Button
            size="sm" onClick={save} disabled={saving}
            data-testid="careplan-portal-publish-save"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Tick the sections you want to publish to the participant/family portal.
          Nothing is shared unless explicitly ticked.
        </p>
        <div className="space-y-1">
          {allOptions.length === 0 ? (
            <p className="text-xs text-muted-foreground">No sections to publish.</p>
          ) : allOptions.map((s) => (
            <label
              key={s.key}
              className="flex items-center gap-2 text-sm cursor-pointer"
              data-testid={`careplan-portal-publish-row-${s.key}`}
            >
              <Checkbox
                checked={selected.has(s.key)}
                onCheckedChange={() => toggle(s.key)}
                data-testid={`careplan-portal-publish-check-${s.key}`}
              />
              {s.label}
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
